<script setup lang="ts">
import { useQuery } from "@tanstack/vue-query";
import { mdiChevronLeft, mdiChevronRight } from "@mdi/js";
import EventTopLeaguesItem from "./EventTopLeaguesItem.vue";
import { ISport } from "~/types/sports/sport";
import t from "~/utils/translate";
import { useAuth } from "~/store/auth";
import { useBetbook } from "~/store/betbook";
import api from "~/utils/auth/api";
import { IFavourite } from "~/types/player/sport-favourite";
import { useSettings } from "~/store/settings";
import { forEach } from "lodash";

interface Props {
	sport: ISport;
}

const settings = useSettings();
const store = useBetbook();
const auth = useAuth();
const props = defineProps<Props>();

const shouldFetch = computed(() => auth.loggedIn && !!props.sport);
const { data: favourites } = useQuery<{ userAccountFavourites: IFavourite[] }>({
	queryKey: ["league-favourites", props.sport.sportId],
	queryFn: () =>
		api({
			url: "/UserAccountFavourite/GetUserAccountFavourite",
			type: "player",
			options: { method: "GET" },
		}),
	enabled: shouldFetch,
	onSuccess: (data) => {
		const favs = data.userAccountFavourites.filter((l) => l.favouriteType === "League" && l.sportId === props.sport?.sportId);
		store.favourites.league = favs.map((f) => f);
	},
});

const leagueFavouriteCount = computed(() => store.favourites.league.length);
const shouldFetchValidatedFavourites = computed(() => auth.loggedIn && !!props.sport && !!favourites.value);

const { data: validatedFavourites } = useQuery<ISport["topLeagues"][0][]>({
	keepPreviousData: true,
	queryKey: ["validated-league-favourites", props.sport.sportId, leagueFavouriteCount],
	queryFn: () =>
		api({
			url:
				`/Feeds/league-favourites?CountryCode=${settings.getCountryConfig?.countryIsoTwo}&SportId=${props.sport?.sportId}` +
				(() => {
					let params = "";
					for (let index = 0; index < store.favourites.league.map((l) => l.id).length; index++) {
						params += "&RegionLeagueIds=" + store.favourites.league.map((l) => l.id)[index];
					}
					return params;
				})(),
			type: "sports",
			options: { method: "GET" },
		}),
	enabled: shouldFetchValidatedFavourites,
});

const filteredTopLeagues = computed(() => {
	if (props.sport && props.sport?.topLeagues && props.sport.topLeagues.length > 0) {
		const keys = validatedFavourites.value?.map((x) => x.id) || [];
		return props.sport.topLeagues.filter((x) => !keys.includes(x.id));
	}
});

const navButton = tw`w-6 h-full top-0 absolute transition-all bg-light-50 dark:bg-dark-800 hidden md:flex shadow z-10  items-center justify-center cursor-pointer`;
const { scroll, hasMore } = useScrollableActions("top-events-scroller", 200);
</script>

<template>
	<div v-if="(validatedFavourites && validatedFavourites?.length > 0) || (filteredTopLeagues && filteredTopLeagues?.length > 0)">
		<div class="flex items-center w-full h-8 px-2 text-xs font-bold rounded-t-lg md:text-base text-light-50 bg-dark-600">
			{{ t("top-leagues") }}
		</div>
		<div class="relative">
			<div v-show="hasMore.left" @click="scroll('left')" :class="[navButton, 'left-0 rounded-l-lg']">
				<UiIcon :path="mdiChevronLeft" />
			</div>
			<div v-show="hasMore.right" @click="scroll('right')" :class="[navButton, 'right-0 rounded-r-lg']">
				<UiIcon :path="mdiChevronRight" />
			</div>
			<div
				id="top-events-scroller"
				class="relative flex gap-2 p-1 overflow-x-scroll overflow-y-hidden rounded-b-lg bg-light-200 dark:bg-dark-800 scrollbar-hidden"
			>
				<EventTopLeaguesItem v-if="auth.loggedIn" v-for="fav in validatedFavourites" :key="fav.id" :league="fav" />
				<EventTopLeaguesItem v-for="league in filteredTopLeagues" :key="league.id" :league="league" />
			</div>
		</div>
	</div>
</template>
